import { useCallback, useEffect, useMemo, useState } from "react";
import {
    createVacancyFromHiringRequest,
    getHiringRequests,
} from "../../services/hiringRequestsApi";
import { formatDateRu } from "../../utils/dateFormat";
import { getHiringRequestStatusMeta, matchesHiringRequestSearch } from "../../utils/hiringRequestPresentation";

const BLOCKED_STATUSES = new Set(["закрыта", "завершена", "отменена", "архив", "возвращена на уточнение"]);

export function isRequestAvailableForVacancy(request) {
    return Boolean(request?.id) && !request?.linked_vacancy_id && !BLOCKED_STATUSES.has(request?.status);
}

export default function CreateVacancyFromRequestModal({ onClose, onCreated }) {
    const [requests, setRequests] = useState([]);
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [creatingId, setCreatingId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const data = await getHiringRequests();
            setRequests(Array.isArray(data) ? data : []);
        } catch (requestError) {
            setError(requestError?.message || "Не удалось загрузить заявки на подбор");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const available = useMemo(
        () => requests
            .filter(isRequestAvailableForVacancy)
            .filter((request) => matchesHiringRequestSearch(request, query)),
        [requests, query]
    );

    const linkedCount = requests.filter((request) => request?.linked_vacancy_id).length;

    const createFromRequest = async (request) => {
        setCreatingId(request.id);
        setError("");
        try {
            const vacancy = await createVacancyFromHiringRequest(request.id);
            onCreated?.(vacancy, request);
        } catch (creationError) {
            setError(creationError?.message || "Не удалось создать вакансию из заявки");
        } finally {
            setCreatingId(null);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/65 p-3 backdrop-blur-sm sm:p-6" data-testid="request-vacancy-modal">
            <section className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
                <header className="relative overflow-hidden bg-slate-950 px-6 py-6 text-white sm:px-8">
                    <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-[#cda834]/20 blur-3xl" />
                    <div className="relative flex items-start justify-between gap-5">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#e4c45d]">Заявка → вакансия → HH.ru</p>
                            <h2 className="mt-2 text-2xl font-bold">Создать вакансию из заявки</h2>
                            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">
                                Выберите открытую заявку. Данные перенесутся автоматически, после чего вы проверите обязательные поля HH и сможете опубликовать вакансию.
                            </p>
                        </div>
                        <button type="button" onClick={onClose} aria-label="Закрыть" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/10 text-2xl text-slate-300 hover:bg-white/20 hover:text-white">&times;</button>
                    </div>
                </header>

                <div className="border-b border-slate-200 bg-white px-6 py-4 sm:px-8">
                    <label htmlFor="request-vacancy-search" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">Найти заявку</label>
                    <input id="request-vacancy-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Номер, должность, отдел или руководитель" className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-[#cda834] focus:bg-white focus:ring-2 focus:ring-[#cda834]/20" />
                </div>

                <div className="flex-1 overflow-y-auto bg-slate-50 p-5 sm:p-7">
                    {error && (
                        <div role="alert" className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                            <span>{error}</span>
                            {!creatingId && <button type="button" onClick={load} className="font-semibold underline">Повторить</button>}
                        </div>
                    )}

                    {loading ? (
                        <div className="py-16 text-center text-sm text-slate-500">Загружаем доступные заявки…</div>
                    ) : available.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
                            <h3 className="text-lg font-bold text-slate-900">Подходящих заявок нет</h3>
                            <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-slate-500">
                                {query
                                    ? "Поиск не дал результатов. Измените запрос."
                                    : "Для создания доступны активные заявки без связанной вакансии. Возвращённые, закрытые и отменённые заявки не показываются."}
                            </p>
                            {linkedCount > 0 && !query && <p className="mt-3 text-xs text-slate-400">Уже связаны с вакансиями: {linkedCount}</p>}
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {available.map((request) => {
                                const status = getHiringRequestStatusMeta(request.status);
                                return (
                                    <article key={request.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-[#d8bb55] hover:shadow-md">
                                        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex flex-wrap items-center gap-2 text-xs">
                                                    <span className="font-bold uppercase tracking-wider text-[#a98518]">{request.public_code || `З-${request.id}`}</span>
                                                    <span className="rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-600">{status.label}</span>
                                                </div>
                                                <h3 className="mt-2 truncate text-lg font-bold text-slate-950">{request.position || "Должность не указана"}</h3>
                                                <p className="mt-1 text-sm text-slate-500">{request.department || "Отдел не указан"} · {request.headcount || 0} мест</p>
                                                <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
                                                    <span>Руководитель: <b className="text-slate-700">{request.initiator_name || request.manager_name || "не указан"}</b></span>
                                                    <span>Закрыть до: <b className="text-slate-700">{formatDateRu(request.planned_close_date, "не указано")}</b></span>
                                                </div>
                                            </div>
                                            <button type="button" onClick={() => createFromRequest(request)} disabled={creatingId !== null} className="shrink-0 rounded-xl bg-slate-950 px-5 py-3 text-sm font-bold text-white hover:bg-slate-800 disabled:cursor-wait disabled:opacity-50" data-testid={`create-vacancy-request-${request.id}`}>
                                                {creatingId === request.id ? "Создаём…" : "Создать вакансию"}
                                            </button>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
